import type { Commands } from "@/api/commands";
import type { Backtest } from "@/api/types";
import { formatScriptRef, resolveStrategySource } from "@/lib/script-ref";
import { saveValidatedScript } from "@/lib/script-save";
import { toDateTime } from "@/lib/backtest-run";

type Request = Pick<Commands, "getScriptRevision" | "validateConfigurable" | "updateScript" | "createScript" | "createBacktest">;

/**
 * Copy a lab backtest: its own copy of the script (scripts belong to one
 * backtest), same market, inputs and properties, named "… (copy)".
 */
export async function duplicateBacktest(
  request: Request,
  bt: Backtest,
  scripts: { id: string; source?: string; rev_no?: number }[] = []
): Promise<Backtest> {
  const strategy = { ...(bt.strategy_config || {}) } as Record<string, unknown>;
  const source = await resolveStrategySource(request, String(strategy.source || ""), scripts);
  if (!source.trim()) throw new Error("This backtest has no script to copy.");
  const name = `${bt.name} (copy)`;
  const saved = await saveValidatedScript(request, { scriptId: null, name, source });
  const end = toDateTime(bt.end_date as string | null);

  return (await request.createBacktest({
    name,
    description: bt.description || "",
    market_provider: bt.market_provider,
    market_config: bt.market_config || {},
    symbol: bt.symbol,
    timeframe: bt.timeframe,
    start_date: toDateTime(bt.start_date as string),
    ...(end ? { end_date: end } : {}),
    strategy_provider: bt.strategy_provider || "pinescript",
    strategy_config: { ...strategy, source: formatScriptRef(saved.id, saved.rev_no >= 1 ? saved.rev_no : 1) },
    engine_provider: bt.engine_provider,
    engine_config: bt.engine_config || {},
    ...(bt.recalculate ? { recalculate: bt.recalculate } : {}),
    ...(bt.bar_magnifier ? { bar_magnifier: bt.bar_magnifier } : {}),
    metadata: { ui: { mode: "lab", script_id: saved.id } },
    enabled: false,
  })) as Backtest;
}
